import {
  effectiveFactorValue,
  getCategory,
  leafAllocatedHours,
  mappedContractId,
  walkPlan,
} from '@timelog/pm-shared';
import type {
  BudgetModelConfig,
  ContractConsumption,
  ContractInput,
  ContractType,
  CostRateResolver,
  PlanNode,
  SellRateResolver,
} from '@timelog/pm-shared';

/**
 * How one contract's Own Work budget is doing against the tasks mapped to it
 * — the three figures under the task panel's Contract section (2026-09-28,
 * the pilot's ask):
 *
 * - **distributed**: the mapped tasks' budgets over the contract's Own Work
 *   hours (CALC-11) — the share of the contract's hours the plan has placed;
 * - **allocated**: the mapped tasks' allocated hours (CALC-22) over the same
 *   Own Work hours — the share somebody is committed to;
 * - **uplift**: the contract's Own Work amount over the mapped tasks'
 *   allocated cost (CALC-21) — what was sold against what the staffing costs.
 *
 * Every ratio is `null` when its denominator is zero: no hours, no share; no
 * allocated cost, no uplift. The caller says so instead of dividing.
 *
 * Pure so it is unit-testable in the `node` environment: import this module,
 * never the `planning` barrel.
 */
export type ContractStaffing = {
  /** The contract's Own Work hours (CALC-11). */
  budgetHours: number;
  /** Σ budgets of the leaves mapped to the contract (CALC-15's consumed hours). */
  distributedHours: number;
  /** Σ allocated hours of the leaves mapped to the contract (CALC-22). */
  allocatedHours: number;
  /**
   * Σ of the staffing LINES' cost on the leaves mapped to the contract (hours ×
   * cost rate per line, CALC-21's chain). Not the consumption's cost, which
   * prices an unallocated task by its budget (2026-09-29, the pilot's ask):
   * the uplift is what was sold against what the STAFFING costs.
   */
  allocatedCost: number;
  /**
   * Σ of the staffing LINES' revenue on the leaves mapped to the contract
   * (hours × sell rate per line, CALC-2) — the same figure the task panel's
   * own tile shows. Deliberately not the consumption's amount, which prices an
   * unallocated task by its budget: that made a budget edit move "allocated"
   * revenue, and an average rate over allocated hours nonsense.
   */
  allocatedRevenue: number;
  /** The contract's Own Work amount. */
  ownAmount: number;
  /** Own Work hours not yet placed on a mapped task; negative when over-distributed. */
  distributedRemaining: number;
  /** Own Work hours nobody is allocated to yet; negative when over-allocated. */
  allocatedRemaining: number;
  distributedPct: number | null;
  allocatedPct: number | null;
  uplift: number | null;
};

const round2 = (value: number): number => Math.round(value * 100) / 100;

/** Σ allocated hours per contract, over the leaves mapped to each. */
export const allocatedHoursByContract = (plan: PlanNode[]): Map<string, number> => {
  const sums = new Map<string, number>();
  walkPlan(plan, (node) => {
    if (node.children.length > 0) return;
    const contractId = mappedContractId(node);
    if (contractId === undefined) return;
    sums.set(contractId, round2((sums.get(contractId) ?? 0) + leafAllocatedHours(node)));
  });
  return sums;
};

/** Σ of the staffing lines' cost per contract, over the leaves mapped to each. */
export const allocatedCostByContract = (
  plan: PlanNode[],
  resolveCost: CostRateResolver,
): Map<string, number> => {
  const sums = new Map<string, number>();
  walkPlan(plan, (node) => {
    if (node.children.length > 0) return;
    const contractId = mappedContractId(node);
    if (contractId === undefined) return;
    const cost = node.allocations.reduce(
      (sum, allocation) =>
        sum + allocation.hours * resolveCost(contractId, allocation.roleRef, allocation.personRef),
      0,
    );
    sums.set(contractId, round2((sums.get(contractId) ?? 0) + cost));
  });
  return sums;
};

/** Σ of the staffing lines' revenue per contract, over the leaves mapped to each. */
export const allocatedRevenueByContract = (
  plan: PlanNode[],
  resolveRate: SellRateResolver,
): Map<string, number> => {
  const sums = new Map<string, number>();
  walkPlan(plan, (node) => {
    if (node.children.length > 0) return;
    const contractId = mappedContractId(node);
    if (contractId === undefined) return;
    const revenue = node.allocations.reduce(
      (sum, allocation) =>
        sum +
        allocation.hours * resolveRate(contractId, allocation.roleRef, allocation.personRef).rate,
      0,
    );
    sums.set(contractId, round2((sums.get(contractId) ?? 0) + revenue));
  });
  return sums;
};

// Multiply first: 120 / 400 * 100 is 30.000000000000004 in floating point,
// 120 * 100 / 400 is 30.
const pctOf = (part: number, whole: number): number | null =>
  whole > 0 ? (part * 100) / whole : null;

export const contractStaffing = (
  contractId: string,
  plan: PlanNode[],
  consumption: readonly ContractConsumption[],
  resolveRate: SellRateResolver,
  resolveCost: CostRateResolver,
): ContractStaffing | undefined => {
  const entry = consumption.find((contract) => contract.contractId === contractId);
  if (!entry) return undefined;
  const allocatedHours = allocatedHoursByContract(plan).get(contractId) ?? 0;
  const allocatedRevenue = allocatedRevenueByContract(plan, resolveRate).get(contractId) ?? 0;
  const allocatedCost = allocatedCostByContract(plan, resolveCost).get(contractId) ?? 0;
  return {
    budgetHours: entry.hoursBudget,
    distributedHours: entry.consumedHours,
    allocatedHours,
    allocatedCost,
    allocatedRevenue,
    ownAmount: entry.ownRevenueBudget,
    distributedRemaining: round2(entry.hoursBudget - entry.consumedHours),
    allocatedRemaining: round2(entry.hoursBudget - allocatedHours),
    distributedPct: pctOf(entry.consumedHours, entry.hoursBudget),
    allocatedPct: pctOf(allocatedHours, entry.hoursBudget),
    uplift: allocatedCost > 0 ? entry.ownRevenueBudget / allocatedCost : null,
  };
};

/**
 * What the staffing would earn at the contract's own uplift: the mapped tasks'
 * allocated cost (CALC-21) × the Own Work factor set on the contract
 * (CALC-20). The task panel's forecasted-revenue KPI (2026-10-29, the
 * designer's ask) — distinct from `allocatedRevenue`, which prices the lines
 * at their sell rates. Whole currency units (BR-15).
 */
export const forecastedRevenueAtOwnFactor = (
  staffing: Pick<ContractStaffing, 'allocatedCost'>,
  ownFactor: number,
): number => Math.round(staffing.allocatedCost * ownFactor);

/**
 * The task panel's forecast for one contract (2026-10-29, the designer's ask),
 * by billing type:
 *
 * - **Fixed price**: the revenue is what the staffing would earn at the
 *   contract's own uplift — allocated cost × the Own Work factor set on the
 *   contract — and the factor is the Own Work amount ÷ that cost (what was
 *   sold against what the staffing costs).
 * - **Time & material / retainer** (everything priced by a rate card): the
 *   revenue is Σ allocated revenue on the mapped tasks — the lines at their
 *   sell rates (CALC-2) — and the factor is that revenue ÷ the allocated cost.
 *
 * Both ratios are `null` without allocated cost; the fixed-price factor is
 * also `null` without an Own Work amount (2026-11-02, the designer's ask: a
 * budget must be added before the forecasted factor can be read — ×0.00 with a
 * down arrow said something false), and the fixed-price revenue is `null`
 * without an own factor (no mapped contract). The caller shows a hint.
 */
export type ContractForecast = { revenue: number | null; factor: number | null };

export const contractForecast = (
  staffing: Pick<ContractStaffing, 'allocatedCost' | 'allocatedRevenue' | 'ownAmount'>,
  type: ContractType,
  ownFactor: number | null,
): ContractForecast => {
  const hasCost = staffing.allocatedCost > 0;
  if (type === 'fixed') {
    return {
      revenue: ownFactor === null ? null : forecastedRevenueAtOwnFactor(staffing, ownFactor),
      factor:
        hasCost && staffing.ownAmount > 0 ? staffing.ownAmount / staffing.allocatedCost : null,
    };
  }
  return {
    revenue: Math.round(staffing.allocatedRevenue),
    factor: hasCost ? staffing.allocatedRevenue / staffing.allocatedCost : null,
  };
};

/**
 * The Own Work uplift SET on a contract (CALC-20: the stored factor while the
 * section is editable, the configuration's default otherwise) — what the
 * forecasted uplift is compared against (2026-09-29, the pilot's ask).
 */
export const contractOwnFactor = (contract: ContractInput, config: BudgetModelConfig): number =>
  effectiveFactorValue(config, 'own', getCategory(contract.document, 'own').targetFactor);
